import { isTradingExperience } from '@/features/onboarding/lib/welcome-tips'
import type { TutorialProgress } from '@/features/tutorial/lib/tutorial-progress'
import { isTutorialId } from '@/features/tutorial/lib/tutorials'
import type { WelcomeGuideState } from '@/state/onboarding'
import type { OrderTypeGuideSeen } from '@/state/order-type-guide'

/** The backend component-preference record (per user) the guides' state lives in. */
export const GUIDES_COMPONENT_ID = 'user-guides'

/**
 * Everything "have you seen this yet" for one user: the order form's first-use order type
 * animations, the welcome tour, the preset tour, and how far each tutorial got. Stored per
 * user, not per page, so it holds wherever the site is opened.
 */
export interface GuidePreferences {
  orderTypeGuide: OrderTypeGuideSeen
  welcomeGuide: WelcomeGuideState
  tutorialProgress: TutorialProgress
  presetTour: boolean
}

export const EMPTY_GUIDE_PREFERENCES: GuidePreferences = {
  orderTypeGuide: {},
  welcomeGuide: { dismissed: false, experience: null },
  tutorialProgress: {},
  presetTour: false,
}

const KEYS = {
  orderTypeGuide: 'orderTypeGuide',
  welcomeGuide: 'welcomeGuide',
  tutorialProgress: 'tutorialProgress',
  presetTour: 'presetTour',
} as const

/**
 * Combines two copies without losing anything: a guide seen in either stays seen, and each
 * tutorial keeps its highest acknowledged step count. Every field only ever moves one way,
 * so merging the
 * server copy into a local one that changed meanwhile can't undo either side.
 * On conflict `a`'s experience answer wins.
 */
export function mergeGuidePreferences(a: GuidePreferences, b: GuidePreferences): GuidePreferences {
  const orderTypeGuide = { ...b.orderTypeGuide }
  for (const [key, seen] of Object.entries(a.orderTypeGuide)) {
    if (seen) orderTypeGuide[key as keyof OrderTypeGuideSeen] = true
  }
  const tutorialProgress = { ...b.tutorialProgress }
  for (const [id, step] of Object.entries(a.tutorialProgress)) {
    const other = tutorialProgress[id as keyof TutorialProgress]
    if (step !== undefined && (other === undefined || step > other)) {
      tutorialProgress[id as keyof TutorialProgress] = step
    }
  }
  return {
    orderTypeGuide,
    welcomeGuide: {
      dismissed: a.welcomeGuide.dismissed || b.welcomeGuide.dismissed,
      experience: a.welcomeGuide.experience ?? b.welcomeGuide.experience,
    },
    tutorialProgress,
    presetTour: a.presetTour || b.presetTour,
  }
}

export function serializeGuidePreferences(prefs: GuidePreferences): Record<string, string> {
  return {
    [KEYS.orderTypeGuide]: JSON.stringify(prefs.orderTypeGuide),
    [KEYS.welcomeGuide]: JSON.stringify(prefs.welcomeGuide),
    [KEYS.tutorialProgress]: JSON.stringify(prefs.tutorialProgress),
    [KEYS.presetTour]: JSON.stringify(prefs.presetTour),
  }
}

/** The stored record, with anything malformed or unknown dropped rather than trusted. */
export function parseGuidePreferences(
  config: Record<string, string> | undefined
): GuidePreferences {
  const orderTypeGuide: OrderTypeGuideSeen = {}
  for (const [key, seen] of Object.entries(parseObject(config?.[KEYS.orderTypeGuide]))) {
    if (seen === true) orderTypeGuide[key as keyof OrderTypeGuideSeen] = true
  }

  const welcome = parseObject(config?.[KEYS.welcomeGuide])
  const welcomeGuide: WelcomeGuideState = {
    dismissed: welcome.dismissed === true,
    experience: isTradingExperience(welcome.experience) ? welcome.experience : null,
  }

  const tutorialProgress: TutorialProgress = {}
  for (const [id, step] of Object.entries(parseObject(config?.[KEYS.tutorialProgress]))) {
    if (isTutorialId(id) && Number.isInteger(step) && (step as number) >= 0) {
      tutorialProgress[id] = step as number
    }
  }

  const presetTour = config?.[KEYS.presetTour] === 'true'

  return { orderTypeGuide, welcomeGuide, tutorialProgress, presetTour }
}

function parseObject(raw: string | undefined): Record<string, unknown> {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as unknown
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {}
  } catch {
    return {}
  }
}
